import * as React from 'react';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from 'shared/components/ui/table';
import {
    Card, CardContent, CardHeader, CardTitle, CardDescription
} from 'shared/components/ui/card';
import {
    Button
} from 'shared/components/ui/button';
import {
    Pencil, Trash2
} from 'lucide-react';
import {
 Column
} from './types/builderTables.types';
import {
 TablePreviewProps
} from './types/builderTables.types';
// Define types for column data structure
// interface Column {
//     name: string;
//     description?: string;
//     type?: string;
//     fieldAccess?: string;
//     constraints?: string[];
//     defaultValue?: string;
//     validation?: boolean;
//     validationEquation?: string;
//     fieldLength?: number;
//     dateFormat?: string;
//     values?: string[];
//     equation?: string;
//     foreignKeyType?: string;
//     foreignKeyTable?: string;
// }

// interface TablePreviewProps {
//     columns: Column[];
//     tableName: string | null;
//     editColumn: (index: number) => void;
//     deleteColumn: (index: number) => void;
//     editable: boolean;
// }

export function TablePreview({
    columns, tableName, editColumn, deleteColumn, editable
}: TablePreviewProps) {
    const previewFields = [
        {
 key: 'name',
label: 'Column Name'
},
        {
 key: 'description',
label: 'Description'
},
        {
 key: 'type',
label: 'Data Type'
},
        {
 key: 'fieldAccess',
label: 'Field Access'
},
        {
 key: 'constraints',
label: 'Constraints'
},
        {
 key: 'defaultValue',
label: 'Default Value'
},
        {
 key: 'validation',
label: 'Validation'
},
        {
 key: 'validationEquation',
label: 'Validation Equation'
},
        {
 key: 'fieldLength',
label: 'Field Length'
},
        {
 key: 'dateFormat',
label: 'Date Format'
},
        {
 key: 'values',
label: 'Enum Values'
},
        {
 key: 'equation',
label: 'Equation'
},
        {
 key: 'foreignKeyType',
label: 'Foreign Key Type'
},
        {
 key: 'foreignKeyTable',
label: 'Reference Table'
},
    ];

    // Type for checking if a value exists (can be an array, boolean, or general value)
    const hasValue = (col: Column, key: keyof Column): boolean => {
        const value = col[key];
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === 'boolean') return value;
        return value !== undefined && value !== null && value !== '';
    };

    // Filter fields to show based on the presence of data in columns
    // const fieldsToShow = previewFields.filter(field => {
    //     if (field.key === 'name') return true; // Always show the 'name' field
    //     return columns.some(col => hasValue(col, field.key));
    // });
const fieldsToShow = previewFields.filter(field => {
    if (field.key === 'name') return true; // Always show the 'name' field
    return columns.some(col => hasValue(col, field.key as keyof Column)); // Cast field.key to keyof Column
});

    if (columns.length === 0) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle>{tableName ? `Table Preview: ${tableName}` : 'Table Preview'}</CardTitle>
                    <CardDescription>Add columns to see how your table will look</CardDescription>
                </CardHeader>
                <CardContent className="text-center text-muted-foreground py-8">
                    No columns defined yet. Start by adding columns to your table.
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle>{tableName ? `Table Preview: ${tableName}` : 'Table Preview'}</CardTitle>
                <CardDescription>Preview of your column definitions</CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
                <Table className="w-full">
                    <TableHeader>
                        <TableRow className="flex">
                            {fieldsToShow.map((field, index) => (
                                <TableHead
                                    key={field.key}
                                    className={`flex-1 ${index === fieldsToShow.length - 1 ? 'w-full' : ''}`}
                                >
                                    {field.label}
                                </TableHead>
                            ))}
                            {editable && (
                                <TableHead className="w-[120px]">Actions</TableHead>
                            )}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {columns.map((col, idx) => (
                            <TableRow key={idx} className="flex">
                                {fieldsToShow.map((field, fieldIdx) => {
                                    let value = col[field.key];

                                    // Format specific fields for better display
                                    if (field.key === 'constraints' && Array.isArray(value)) {
                                        value = value.join(', ');
                                    } else if (field.key === 'validation') {
                                        value = value ? 'Yes' : 'No';
                                    } else if (field.key === 'values' && Array.isArray(value)) {
                                        value = value.join(', ');
                                    }

                                    return (
                                        <TableCell
                                            key={field.key}
                                            className={`flex-1 ${fieldIdx === fieldsToShow.length - 1 ? 'w-full' : ''}`}
                                        >
                                            {value || '-'}
                                        </TableCell>
                                    );
                                })}
                                {editable && (
                                    <TableCell className="w-[120px] text-right whitespace-nowrap">
                                        <div className="flex gap-2">
                                            <Button size="icon" variant="outline" onClick={() => editColumn(idx)}>
                                                <Pencil className="h-4 w-4" />
                                            </Button>
                                            <Button size="icon" variant="destructive" onClick={() => deleteColumn(idx)}>
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
}
